import { useEffect, useCallback } from 'react';
import { useSelector, store } from '~/store';
import { getUserLimitsApi } from '~/api';
import { ApiResponseEnum, apiResponseHandler } from '~/utils/api';
import {
  fetchUserLimitsStart,
  fetchUserLimitsSuccess,
  fetchUserLimitsFailure,
  clearUserLimits,
} from '~/store/slices/userLimits';

export interface UserLimitsResponse {
  can_create: {
    invoice: boolean;
    client: boolean;
    estimate: boolean;
  };
}

interface UseUserLimitsReturn {
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
  canCreateInvoice: boolean;
  canCreateClient: boolean;
  canCreateEstimate: boolean;
}

const fetchLimits = async () => {
  const { hasFetched, isLoading } = store.getState().userLimits;

  if (hasFetched || isLoading) return;

  store.dispatch(fetchUserLimitsStart());

  try {
    const response = await getUserLimitsApi();
    const result = await apiResponseHandler<UserLimitsResponse>(response);

    if (result.type === ApiResponseEnum.Success) {
      store.dispatch(
        fetchUserLimitsSuccess({
          invoice: result.data.can_create.invoice,
          client: result.data.can_create.client,
          estimate: result.data.can_create.estimate,
        }),
      );
    } else {
      store.dispatch(fetchUserLimitsFailure(result.error || 'Failed to fetch user limits'));
    }
  } catch (err) {
    store.dispatch(fetchUserLimitsFailure('An error occurred while fetching user limits'));
    console.error('Error fetching user limits:', err);
  }
};

const forceRefetchLimits = async () => {
  store.dispatch(clearUserLimits());

  store.dispatch(fetchUserLimitsStart());

  try {
    const response = await getUserLimitsApi();

    const result = await apiResponseHandler<UserLimitsResponse>(response);

    if (result.type === ApiResponseEnum.Success) {
      store.dispatch(
        fetchUserLimitsSuccess({
          invoice: result.data.can_create.invoice,
          client: result.data.can_create.client,
          estimate: result.data.can_create.estimate,
        }),
      );
    } else {
      store.dispatch(fetchUserLimitsFailure(result.error || 'Failed to fetch user limits'));
    }
  } catch (err) {
    store.dispatch(fetchUserLimitsFailure('An error occurred while fetching user limits'));
    console.error('Error fetching user limits:', err);
  }
};

export const useUserLimits = (shouldFetch = true): UseUserLimitsReturn => {
  const { isLoading, error, canCreateInvoice, canCreateClient, canCreateEstimate } = useSelector(
    state => state.userLimits,
  );

  useEffect(() => {
    if (shouldFetch) {
      fetchLimits();
    }
  }, [shouldFetch]);

  const refetch = useCallback(() => {
    forceRefetchLimits();
  }, []);

  return {
    isLoading,
    error,
    refetch,
    canCreateInvoice: shouldFetch ? canCreateInvoice : false,
    canCreateClient: shouldFetch ? canCreateClient : false,
    canCreateEstimate: shouldFetch ? canCreateEstimate : false,
  };
};
